import api from "API/api";
import {
  Box,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {DataGrid} from "@mui/x-data-grid";
import colors from "assets/theme/base/colors";
import {useLocation} from "react-router-dom";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import useAnalyticsStore from "store/analyticsStore";
import VideoPreviewModal from "../../../components/Modals/VideoPreviewModal/VideoPreviewModal";
import { S3SignedUrl } from "../../../components/Common/s3Bucket";
import { Pagination } from "../../../components/Common/pagination";
import { ImageCell } from "../../../components/Common/imageCell";

const AnalysisData = () => {
  const location = useLocation();
  const [page, setPage] = useState(0);
  const { white, gradients } = colors;
  const [downloadTimer, setDownloadTimer] = useState(null);
  const [records, setRecords] = useState(location.state?.records || []);
  const [loading, setLoading] = useState(false);
  const { violationType, selectedLocation } = location.state;
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const { paginationUrls, setPaginationUrls } = useAnalyticsStore();
  const BUCKET = "benchmark-violation-videos";
  const fetchViolationRecords = async (url = null) => {
    let apiUrl = url;
    if (!apiUrl) {
      apiUrl = `/violations?site_id=${selectedLocation.site_id}&violation_type=${violationType}`;
    }
    if (!violationType || !selectedLocation) return;
    setLoading(true);

    try {
      const response = await api.get(apiUrl);
      const nextUrl = response?.data?.next;
      const prevUrl = response?.data?.previous;
      const results = response?.data?.results?.data || [];

      const filteredRecords = [];

      results.forEach((item) => {
        item.violations.forEach((viol) => {
          if (viol.violation === violationType) {
            viol.records.forEach((record) => {
              filteredRecords.push({
                ...record,
                violation: viol.violation,
              });
            });
          }
        });
      });
      setPaginationUrls({
        next: nextUrl,
        prev: prevUrl,
      });
      setRecords(filteredRecords);
    } catch (err) {
      console.error("Failed to fetch data by violation type", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchViolationRecords();
  }, [violationType, selectedLocation]);

  const formatDateForDownload = (dateString) => {
    if (!dateString) return "undefined";

    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const fetchAndDownloadVideo = async (url) => {
    try {
      setIsDownloading(true);
      const key = url.replace("s3://benchmark-violation-videos/", "");
      const signedUrl = await S3SignedUrl(BUCKET, key);
      const timer = setTimeout(() => downloadVideo(signedUrl), 100);
      setDownloadTimer(timer);
    } catch (error) {
      console.error("Failed to fetch video:", error);
    }
  };

  const downloadVideo = (videoUrl) => {
    if (!videoUrl) {
      return;
    }

    fetch(videoUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `${violationType}-${
          selectedLocation?.location
        }-${formatDateForDownload(records[0]?.created_at)}.mp4`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => console.error("Error in downloading the video", err))
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const handleDownload = (url, id) => {
    setSelectedVideoUrl(id);
    clearTimeout(downloadTimer);
    fetchAndDownloadVideo(url);
  };

  const _dataGrid = records?.map((item, index) => ({
    id: item.id || "-",
    description: item.description || "-",
    violation: item.violation || "-",
    area: item.area || "-",
    date: item.created_at || "-",
    device: item.device || "-",
    violation_clip: item.violation_clip || null,
    violation_image: item.violation_image,
  }));

  const columns = [
    { field: "violation", headerName: "Violation", width: 200 },
    { field: "description", headerName: "Description", width: 380 },
    { field: "area", headerName: "Area", width: 150 },
    { field: "device", headerName: "Device", width: 150 },
    {
      field: "date",
      headerName: "Created At",
      width: 200,
      renderCell: (params) => {
        return (
          <Stack
            direction="column"
            gap={0.4}
            justifyContent="center"
            height={1}
            sx={{ color: "text.secondary" }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              {params.row.date?.split(" ")[0]}
            </Typography>
            <Typography variant="caption">
              {params.row.date?.split(" ")[1]}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "violation_image",
      headerName: "Image",
      width: 220,
      renderCell: (params) =>
        params?.row?.violation_image ? (
          <ImageCell src={params?.row?.violation_image} bucket={BUCKET}/>
        ) : (
          "No Image Available"
        ),
    },
    {
      field: "video",
      headerName: "Stream Preview",
      width: 150,
      renderCell: (params) => (
        <IconButton
          sx={{ fontSize: "30px" }}
          onClick={() => {
            setPreviewModalOpen(true);
            setSelectedVideoUrl(params?.row?.violation_clip);
          }}
        >
          <Icon sx={{ color: "info.focus" }}>preview</Icon>
        </IconButton>
      ),
},
    {
      field: "violation_clip",
      headerName: "Download",
      width: 200,
      renderCell: (params) => (
        <>
          <Button
            variant="outlined"
            onClick={() => {
              handleDownload(params.value, params.id);
            }}
            sx={{
              width: "140px",
              height: "38px",
              fontSize: "14px",
              fontWeight: "500",
              background: `${gradients.info.main}`,
              color: white.main,
              border: "none",
              marginRight: "5px",
              marginBottom: "0",
              "&:hover": {
                background: `${gradients.info.state} !important`,
                color: white.main,
              },
            }}
          >
            {isDownloading && selectedVideoUrl === params.id ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{ color: "white !important" }}
                />
              </>
            ) : (
              <>
                <Icon sx={{ marginRight: "5px" }}>download</Icon> Stream
              </>
            )}
          </Button>
        </>
      ),
    },
  ];

  const NoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        fontSize: "20px",
        fontWeight: 500,
        color: "#666",
        opacity: "0.4",
      }}
    >
      No Data Found
    </Box>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box>
        <DataGrid
          maxColumns={6}
          rowHeight={60}
          rows={_dataGrid}
          columns={columns}
          loading={loading}
          rowCount={records?.length}
          rowLength={records?.length}
          disableRowSelectionOnClick
          hideFooterPagination
          slots={{
            noRowsOverlay: NoRowsOverlay,
          }}
          pageSizeOptions={[10, 20, 30, 40, 50, 100]}
          sx={{
            marginTop: 3,
            boxShadow: 1,
            fontSize: "15px",
            borderRadius: "8px",
            "& .MuiDataGrid-overlayWrapperInner": { background: "white" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#fff",
              fontWeight: "500",
            },
            "& .MuiDataGrid-cell": {
              outlineOffset: "none !important",
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader": {
              background: "#063863",
              "& .MuiSvgIcon-root": { color: "#fff !important" },
            },
            "& .MuiDataGrid-row": {
              "&:nth-of-type(odd)": { backgroundColor: "#f8f9fa" },
              "&:hover": { backgroundColor: "rgb(25 108 215 / 0.02)" },
            },
          }}
        />

        <Pagination
          handlePrevPage={() => {
            if (paginationUrls.prev) {
              setPage((p) => p - 1);
              fetchViolationRecords(paginationUrls.prev);
            }
          }}
          handleNextPage={() => {
            if (paginationUrls.next) {
              setPage((p) => p + 1);
              fetchViolationRecords(paginationUrls.next);
            }
          }}
          paginationUrls={paginationUrls}
        />

        {previewModalOpen && (
          <VideoPreviewModal
            handleOpen={previewModalOpen}
            handleClose={() => setPreviewModalOpen(false)}
            videoUrl={selectedVideoUrl}
          />
        )}
      </Box>
    </DashboardLayout>
  );
};

export default AnalysisData;
